import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

export default function PageTransition() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [particles, setParticles] = useState<{ id: number; x: number; y: number; size: number; delay: number; duration: number }[]>([]);

  const triggerTransition = useCallback(() => {
    const newParticles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 6 + 2,
      delay: Math.random() * 0.4,
      duration: Math.random() * 0.6 + 0.6,
    }));
    setParticles(newParticles);
    setVisible(true);
    const timer = setTimeout(() => setVisible(false), 900);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    triggerTransition();
  }, [location.pathname, triggerTransition]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] pointer-events-none"
      style={{ animation: 'shimmerFade 900ms ease-in-out forwards' }}
    >
      {/* Gold shimmer base */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, rgba(10,8,4,0.97) 0%, rgba(26,18,8,0.95) 40%, rgba(15,12,6,0.97) 70%, rgba(28,20,8,0.95) 100%)',
        }}
      />

      {/* Sweeping gold light beam */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(105deg, transparent 0%, transparent 30%, rgba(212,175,55,0.18) 45%, rgba(245,226,122,0.28) 50%, rgba(212,175,55,0.18) 55%, transparent 70%, transparent 100%)',
          animation: 'sweepBeam 900ms ease-in-out forwards',
        }}
      />

      {/* Secondary shimmer sweep */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(75deg, transparent 0%, transparent 20%, rgba(212,175,55,0.08) 40%, rgba(245,226,122,0.14) 50%, rgba(212,175,55,0.08) 60%, transparent 80%, transparent 100%)',
          animation: 'sweepBeam2 900ms ease-in-out forwards',
        }}
      />

      {/* Radial centre glow */}
      <div
        className="absolute inset-0 flex items-center justify-center"
      >
        <div
          style={{
            width: '60vw',
            height: '60vw',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0.04) 40%, transparent 70%)',
            filter: 'blur(40px)',
            animation: 'glowBurst 900ms ease-in-out forwards',
          }}
        />
      </div>

      {/* Gold dust particles */}
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: `rgba(212,175,55,${Math.random() * 0.5 + 0.3})`,
            boxShadow: `0 0 ${p.size * 2}px rgba(212,175,55,0.6)`,
            animation: `particleBurst ${p.duration}s ease-out ${p.delay}s forwards`,
            opacity: 0,
          }}
        />
      ))}

      {/* Horizontal gold lines */}
      {[15, 35, 50, 65, 85].map((top, i) => (
        <div
          key={i}
          className="absolute left-0 right-0"
          style={{
            top: `${top}%`,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.4), rgba(245,226,122,0.6), rgba(212,175,55,0.4), transparent)',
            animation: `lineFlash 900ms ease-in-out ${i * 60}ms forwards`,
            opacity: 0,
          }}
        />
      ))}

      {/* Centre logo mark */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ animation: 'logoFlash 900ms ease-in-out forwards' }}
      >
        <div
          className="flex flex-col items-center gap-2"
          style={{ opacity: 0, animation: 'logoFlash 900ms ease-in-out forwards' }}
        >
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: '1px solid rgba(212,175,55,0.5)',
              boxShadow: '0 0 30px rgba(212,175,55,0.4)',
            }}
          >
            <img
              src="https://static.readdy.ai/image/bff3983fcac7598cf15bb5173700e0d7/b58838733802ef1d20431284565f42b3.jpeg"
              alt="House of FMR"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          <span
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: '11px',
              letterSpacing: '0.4em',
              background: 'linear-gradient(135deg, #d4af37, #f5e27a, #d4af37)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            HOUSE OF FMR
          </span>
        </div>
      </div>

      <style>{`
        @keyframes shimmerFade {
          0%   { opacity: 0; }
          15%  { opacity: 1; }
          70%  { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes sweepBeam {
          0%   { transform: translateX(-100%); opacity: 0; }
          20%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
        @keyframes sweepBeam2 {
          0%   { transform: translateX(100%); opacity: 0; }
          20%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateX(-100%); opacity: 0; }
        }
        @keyframes glowBurst {
          0%   { opacity: 0; transform: scale(0.5); }
          40%  { opacity: 1; transform: scale(1.2); }
          70%  { opacity: 0.8; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.8); }
        }
        @keyframes particleBurst {
          0%   { opacity: 0; transform: scale(0) translateY(0); }
          30%  { opacity: 1; transform: scale(1.5) translateY(-10px); }
          100% { opacity: 0; transform: scale(0.5) translateY(-30px); }
        }
        @keyframes lineFlash {
          0%   { opacity: 0; transform: scaleX(0); }
          30%  { opacity: 1; transform: scaleX(1); }
          70%  { opacity: 0.6; transform: scaleX(1); }
          100% { opacity: 0; transform: scaleX(0.8); }
        }
        @keyframes logoFlash {
          0%   { opacity: 0; transform: scale(0.8); }
          25%  { opacity: 1; transform: scale(1.05); }
          65%  { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}
